import type { ReactElement } from 'react';

import type { PendingPreferenceProposal } from '@elara/client-core/chat/pending-preference-proposals';
import type { PreferenceProposalChoice } from '@elara/domain/models/schemas/widget-schemas';

import { useCallback, useEffect, useRef } from 'react';

import { findPendingPreferenceProposals } from '@elara/client-core/chat/pending-preference-proposals';
import { getErrorReporter } from '@elara/client-core/lib/error-reporting';
import { useStateStore } from '@elara/client-core/lib/signals-utils';
import { cn } from '@elara/client-core/lib/utils';

import { useChatsState, useMessageViewState, useUserProfileState } from '#states/hooks.js';
import { PreferenceProposalPrompt, PreferenceSavedNotice } from '#ui/chat/PreferenceProposalPrompt.js';

/** Gap between the floating prompt and the composer (`mb-2` in `ChatInputView`), counted as covered feed. */
const PROMPT_GAP_PX = 8;
/** How long the "Thanks. We’ll remember that." notice stays up before dropping away. */
const SAVED_NOTICE_MS = 2000;
/** Matches the cards' exit transition (`duration-200`). */
const EXIT_MS = 200;

/** After the queue is answered: nothing, the saved notice, or the notice playing its exit. */
type NoticePhase = 'hidden' | 'shown' | 'leaving';

/** The last prompt, kept on screen for its exit after a run that ended without anything remembered. */
interface DismissingPrompt {
  proposal: PendingPreferenceProposal;
  position: number;
  total: number;
}

/**
 * Surfaces the chat's undecided "remember this?" proposals above the composer, one at a time, oldest
 * first. The web feed no longer renders these cards (see `MessageContentRenderer`), so this is the only
 * place a guest answers them. Answered ids are held locally so the next proposal shows immediately,
 * rather than waiting for the Firestore snapshot that flips the card's `decision`.
 *
 * `whenIdle` renders in the slot while there's nothing to ask or confirm, so lower-priority prompts
 * (the notification offer) share the slot without ever overlapping these.
 */
export function PendingPreferenceProposals({
  className,
  whenIdle,
}: {
  className?: string;
  whenIdle?: ReactElement;
}) {
  const chatsState = useChatsState();
  const userProfileState = useUserProfileState();
  const messageViewState = useMessageViewState();
  const observerRef = useRef<ResizeObserver | undefined>(undefined);
  const elementRef = useRef<HTMLDivElement | undefined>(undefined);

  // Publish how much of the feed the floating prompt covers so the "scroll to latest" button can clear
  // it. Tracks resizes (wrapping statements, the error line) and resets to 0 when the prompt goes away.
  const measureRef = useCallback(
    (element: HTMLDivElement | null) => {
      observerRef.current?.disconnect();
      observerRef.current = undefined;
      elementRef.current = element ?? undefined;
      if (!element) {
        messageViewState.composerOverlayHeight = 0;
        return;
      }
      const publish = () => {
        messageViewState.composerOverlayHeight = element.offsetHeight + PROMPT_GAP_PX;
      };
      publish();
      observerRef.current = new ResizeObserver(publish);
      observerRef.current.observe(element);
    },
    [messageViewState],
  );
  const state = useStateStore({
    savingChoice: undefined as PreferenceProposalChoice | undefined,
    error: undefined as string | undefined,
    decidedIds: [] as string[],
    /** Answers given in the current run, for the "n of m" counter; reset when the run ends. */
    done: 0,
    /** Whether any answer in the current run of proposals was "yes", so the run ends with the notice. */
    rememberedAny: false,
    notice: 'hidden' as NoticePhase,
    /** The last prompt's height, held by the notice so the bar keeps its size when it swaps in. */
    noticeHeight: undefined as number | undefined,
    dismissing: undefined as DismissingPrompt | undefined,
  });

  // Walk the notice through shown → leaving → hidden. Keyed on the phase so each step owns one timer.
  const noticePhase = state.notice;
  useEffect(() => {
    if (noticePhase === 'hidden') return;
    const timer = setTimeout(
      () => (state.notice = noticePhase === 'shown' ? 'leaving' : 'hidden'),
      noticePhase === 'shown' ? SAVED_NOTICE_MS : EXIT_MS,
    );
    return () => clearTimeout(timer);
  }, [noticePhase, state]);

  // Remove the declined last prompt once its exit has played.
  const dismissing = state.dismissing;
  useEffect(() => {
    if (!dismissing) return;
    const timer = setTimeout(() => (state.dismissing = undefined), EXIT_MS);
    return () => clearTimeout(timer);
  }, [dismissing, state]);

  const pending = findPendingPreferenceProposals(chatsState.messagesList).filter(
    (proposal) => !state.decidedIds.includes(proposal.proposalId),
  );
  const current = pending[0];
  // Falls back to the dismissing prompt so the same element stays mounted and transitions out.
  const shown = current ?? dismissing?.proposal;

  if (!shown) {
    if (noticePhase === 'hidden') return whenIdle ?? null;
    return (
      <div ref={measureRef} className={className}>
        <PreferenceSavedNotice isLeaving={noticePhase === 'leaving'} minHeight={state.noticeHeight} />
      </div>
    );
  }

  const decide = async (choice: PreferenceProposalChoice) => {
    if (!current || state.savingChoice) return;
    state.savingChoice = choice;
    state.error = undefined;
    try {
      await userProfileState.decidePreferenceProposal({ ...current, decision: choice });
      state.decidedIds = [...state.decidedIds, current.proposalId];
      state.done += 1;
      state.rememberedAny ||= choice === 'yes';
      // At the end of the run, confirm if anything was saved to the profile; otherwise just slide away.
      if (pending.length === 1) {
        if (state.rememberedAny) {
          state.noticeHeight = elementRef.current?.getBoundingClientRect().height;
          state.notice = 'shown';
        } else {
          state.dismissing = { proposal: current, position: state.done, total: state.done };
        }
        state.rememberedAny = false;
        state.done = 0;
      }
    } catch (error) {
      getErrorReporter().captureException(error);
      state.error = 'We couldn’t update your profile. Please try again.';
    } finally {
      state.savingChoice = undefined;
    }
  };

  return (
    <div ref={measureRef} className={cn(
        // Slides up once when the bar appears. The wrapper stays mounted while later proposals swap in
        // (only the prompt is keyed), so the entrance doesn't replay for each one.
        'animate-in fade-in slide-in-from-bottom-2 duration-200 ease-out motion-reduce:animate-none',
        className,
      )}
    >
      <PreferenceProposalPrompt
        // Remount per proposal so focus and busy styling never carry over to the next one.
        key={shown.proposalId}
        statement={shown.statement}
        position={current ? state.done + 1 : (dismissing?.position ?? 1)}
        total={current ? state.done + pending.length : (dismissing?.total ?? 1)}
        isLeaving={!current}
        savingChoice={state.savingChoice}
        error={state.error}
        onDecide={(choice) => void decide(choice)}
      />
    </div>
  );
}
